/**
 * Gewährleistung eines Mangels (tri-server docs/claims.md): Vertrag, Verjährungsende, Resttage und
 * die Herleitung. Erscheint nur, wenn der Mangel einen Vertrag hat und der Server-Schalter an ist
 * (sonst antwortet die Route 404 und der Abschnitt bleibt weg).
 *
 * Unter jedem Ende steht der Hinweis „keine Rechtsberatung“ — der Regelsatz ist nicht juristisch
 * abgenommen (`legallyApproved: false`).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ShieldAlert } from "lucide-react";

import { useClaimsAPI, type ConstructionContractView, type DefectLimitation } from "../../ClaimsAPI";
import { formatLegalDate, warrantyUrgency } from "./claimModel";

import { Badge } from "@/components/ui/badge";

interface Props {
  customer: string;
  defectId: string;
  /** Neu laden, wenn sich der Mangel ändert (z. B. verworfen). */
  revision?: unknown;
}

export default function WarrantySection({ customer, defectId, revision }: Props) {
  const { t } = useTranslation();
  const claimsAPI = useClaimsAPI(customer);
  const [view, setView] = useState<DefectLimitation | null>(null);
  const [contract, setContract] = useState<ConstructionContractView | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setContract(null);

    claimsAPI
      .getDefectLimitation(defectId)
      .then(async (result) => {
        if (cancelled || !result?.contract) return;
        setView(result);
        const loaded = await claimsAPI.getContract(result.contract);
        if (!cancelled) setContract(loaded);
      })
      // Der Anspruchsteil ist eine Zugabe — scheitert er, bleibt der Mangel lesbar.
      .catch(() => !cancelled && setView(null));

    return () => {
      cancelled = true;
    };
  }, [customer, defectId, revision]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!view) return null;

  const limitation = view.limitation;
  const urgency = warrantyUrgency(limitation);
  const contractLine = contract
    ? t("claims.warranty.contract", {
        name: contract.name,
        legalType: contract.legalType ? t(`claims.legalType.${contract.legalType}`) : "—",
      })
    : null;
  const actions = (limitation?.notes ?? []).filter((note) => note.action);

  return (
    <section className="flex flex-col gap-2 rounded-md border p-3" data-guide-id="settings.defects.warranty">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        {view.claimActive ? (
          <ShieldAlert className="h-4 w-4" aria-hidden />
        ) : (
          <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
        )}
        {t("claims.warranty.title")}
        <Badge variant={view.claimActive ? "default" : "outline"}>
          {t(view.claimActive ? "claims.warranty.claimCase" : view.endedBy ? "claims.warranty.ended" : "claims.warranty.noClaim")}
        </Badge>
      </h3>

      {contractLine ? <p className="text-sm">{contractLine}</p> : null}

      {limitation?.end ? (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span>
            {t(limitation.state === "expired" ? "claims.warranty.expiredOn" : "claims.warranty.endsOn", {
              date: formatLegalDate(limitation.end),
            })}
          </span>
          {limitation.state === "running" && limitation.remainingDays != null ? (
            <Badge variant={urgency === "critical" ? "destructive" : urgency === "soon" ? "warning" : "secondary"}>
              {t("claims.warranty.remaining", { count: limitation.remainingDays })}
            </Badge>
          ) : null}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">{t("claims.warranty.undetermined")}</p>
      )}

      {actions.length ? (
        <ul className="list-disc pl-5 text-sm">
          {actions.map((note) => (
            <li key={note.code}>{note.action}</li>
          ))}
        </ul>
      ) : null}

      {limitation?.steps?.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">{t("claims.warranty.derivation")}</summary>
          <ol className="mt-2 list-decimal pl-5">
            {limitation.steps.map((step, index) => (
              <li key={index}>{step.note}</li>
            ))}
          </ol>
        </details>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {view.noLegalAdvice}
        {limitation && !limitation.legallyApproved
          ? ` ${t("claims.warranty.ruleSet", { version: limitation.ruleSetVersion })}`
          : null}
      </p>
    </section>
  );
}
